import { useEffect, useState } from 'react';
import {
  Container,
  Row,
  Col,
  Nav,
  NavItem,
  Modal,
  ModalBody,
  ModalFooter,
  Button,
} from 'reactstrap';
import { Link, useOutletContext, useNavigate, NavLink } from 'react-router-dom';
import {
  useTranslator,
  useAppContext,
  callNativeMobileApp,
  withMobileAppMode,
} from '@front-activerse/common-components';

import Layout from '../../../Layout/Layout';
import TitleBar from '../../../Layout/TitleBar';

import DayPlanReadyAlert from './alerts/DayPlanReadyAlert';
import GoalAlert from './alerts/GoalAlert';
import MeasurementAlert from './alerts/MeasurementAlert';
import TomorrowAlert from './alerts/TomorrowAlert';
import DayPlanNotificationAlert from './alerts/DayPlanNotificationAlert';

const DayPlan = ({
  date,
  children,
  renderTimeline,
  goal,
  isTimeToNagForCurrentMeasurement,
  recipeNotesForNextDay,
  mobileAppMode,
  price,
  mealPlanNavigation,
  setUserFlagData,
  dietPlanIntroSeen,
  dietTypeName,
  mealPlanNotificationData,
  closedNotifications,
  userId,
  getClosedNotificationsValues,
  markNotificationAsClosed,
  loadingMealPlanNotification,
  trackerArticleId,
}) => {
  const { t } = useTranslator();
  const navigate = useNavigate();
  const {
    images: { favMealsIcon, shoppingListIcon, mealPlanPrice, infoIcon, gearIcon },
  } = useAppContext();

  const { trackFirebaseEvent } = useOutletContext();

  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    trackFirebaseEvent('screen_view ', {
      firebase_screen: 'Dayplan',
      meals_tab: 'mealplan',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClickDietType = (event) => {
    event.preventDefault();
    if (mobileAppMode) {
      callNativeMobileApp({
        action: 'open_modal',
        payload: {
          path: `/diet-settings/nutritionSettings/diet-type`,
          modal_variant: 'always_close_btn',
        },
      });
      return;
    }
    navigate(`/diet-settings/nutritionSettings/diet-type`);
  };

  const renderDietType = () => {
    return (
      <p className="mb-0">
        {t('mealPlan.info.diet.title')}{' '}
        <Link to="#" className="diet-type-name" onClick={(event) => handleClickDietType(event)}>
          <span>{dietTypeName}</span>
          <div className="separator"></div>
          <img src={gearIcon} alt="" />
        </Link>
      </p>
    );
  };

  const toggleModal = () => {
    setShowModal(!showModal);
  };

  const renderDayPlanNotificationAlert = () => {
    const userClosedNotification = closedNotifications.some(
      (notification) => notification === mealPlanNotificationData.meal_plan_notification_id
    );

    if (!userClosedNotification && mealPlanNotificationData.meal_plan_notification_active) {
      return (
        <DayPlanNotificationAlert
          userId={userId}
          mealPlanNotificationData={mealPlanNotificationData}
          getClosedNotificationsValues={getClosedNotificationsValues}
          markNotificationAsClosed={markNotificationAsClosed}
        />
      );
    }
    return null;
  };

  return (
    <>
      <Layout id="day-plan">
        <TitleBar showBackBtn={false}>
          {t('meals.navbarTitle')}
          <div className="title-bar-icons">
            <Link
              to="/meals/day-plan/module/shopping-list"
              className="ps-2"
              onClick={() =>
                trackFirebaseEvent('user_action', {
                  action: 'tap-shopping_list',
                  location: 'Dietplan',
                })
              }
            >
              <img src={shoppingListIcon} alt="" />
            </Link>
            <Link
              to="/meals/day-plan/module/favourite-meals"
              className="ps-2"
              onClick={() =>
                trackFirebaseEvent('user_action', {
                  action: 'tap-favourites',
                  location: 'Dietplan',
                })
              }
            >
              <img src={favMealsIcon} alt="" />
            </Link>
          </div>
        </TitleBar>

        <div className="page-template-meal-plan">
          <Container className="py-md-4">
            <Row>
              <Col xl={{ size: 10, offset: 1 }}>
                <div className="page-container">
                  {mealPlanNavigation && mealPlanNavigation.length > 0 && (
                    <div className="sub-navigation text-center">
                      <Container>
                        <Nav className="d-block">
                          {mealPlanNavigation.map((item) => (
                            <NavItem className="w-50">
                              <NavLink to={item.path} replace={true} className="nav-link">
                                {t(item.translationKey)}
                              </NavLink>
                            </NavItem>
                          ))}
                        </Nav>
                      </Container>
                    </div>
                  )}
                  {renderTimeline('top')}

                  <div className="meal-plan-container">
                    {!loadingMealPlanNotification && renderDayPlanNotificationAlert()}
                    <DayPlanReadyAlert
                      setUserFlagData={setUserFlagData}
                      dietPlanIntroSeen={dietPlanIntroSeen}
                    />
                    <GoalAlert goal={goal} />
                    <MeasurementAlert
                      date={date}
                      isTimeToNagForCurrentMeasurement={isTimeToNagForCurrentMeasurement}
                    />
                    <TomorrowAlert recipeNotesForNextDay={recipeNotesForNextDay} />
                  </div>
                  <div className="meal-header pb-0">
                    <h1 className="title-large medium mb-m">{t(`weekday${date.getDay()}`)}</h1>
                    {renderDietType()}
                    {price && (
                      <div className="meal-plan-price">
                        <img src={mealPlanPrice} className="me-s" alt="" />
                        <div className="text-start me-xl">
                          <p className="small mb-0">
                            <strong>
                              {price.amount.toFixed(2)} {price.currency}
                            </strong>
                          </p>
                          <p className="description mb-0">{t('app.mealPlan.mealsCost')}</p>
                        </div>
                        <img src={infoIcon} onClick={toggleModal} alt="" />
                      </div>
                    )}
                  </div>
                  {children()}
                </div>
                <div className="px-3 px-md-0">
                  <hr className="my-4" />
                  <p className="description text-text-variant-color mb-s">
                    {t('tracker.sourceInfo.content')}
                    <br />
                  </p>
                  <p className="description">
                    <Link to={`/knowledge/${trackerArticleId}`}>
                      {t('tracker.sourceInfo.button')}
                    </Link>
                  </p>
                </div>
              </Col>
            </Row>
          </Container>
        </div>
      </Layout>

      <Modal isOpen={showModal} toggle={toggleModal} centered size="md">
        <ModalBody>
          <h4 className="title-medium semi-bold">{t('app.mealPlan.mealsCost.popupTitle')}</h4>
          <p className="text-text-variant-color multi-line mb-0">
            {t('app.mealPlan.mealsCost.popupDescription')}
          </p>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" className="w-100" onClick={toggleModal}>
            {t('app.mealPlan.mealsCost.popupButton')}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default withMobileAppMode(DayPlan);
